import type { Fields } from '@vimeo/bigpicture-client';
import getDeviceType from '@vimeo/ui/core/utils/getDeviceType';
import {
  buildActionBpContext,
  buildProductAnalyticsBpContext,
  buildVideoBpContext,
  sendBpEventWithContexts,
  buildTeamBpContext,
  buildThirdPartyIntegrationBpContext,
  buildWebBpContext,
  type WebBpContext,
} from '@vimeo/use-tracking';
import type { TeamUser } from '@vimeo/viewer';

import type { TSelectItem } from '../types';

export const getTeamRole = (teamUser?: TeamUser) => {
  if (!teamUser || teamUser.ownerId === teamUser.userId) {
    return 'owner';
  } else if (teamUser.plainTextPermissionLevel === 'Admin') {
    return 'admin';
  } else if (teamUser.plainTextPermissionLevel === 'Contributor') {
    return 'contributor';
  } else if (teamUser.plainTextPermissionLevel === 'ContributorPlus') {
    return 'contributor_plus';
  } else if (teamUser.plainTextPermissionLevel === 'Viewer') {
    return 'viewer';
  } else {
    return null;
  }
};

export const bpSelectedDownloadOption = (
  videoId: number,
  quality: string,
  selectedResolution: string,
  audioTrackMeta: TSelectItem,
  pageName?: WebBpContext['page_name'],
  teamUser?: TeamUser,
  path?: string,
  location?: string,
) => {
  const actionContext = buildActionBpContext({
    action_type: 'click',
    feature: null,
  });

  const videoContext = buildVideoBpContext({
    video_id: videoId,
    video_owner_id: teamUser?.ownerId ?? null,
  });

  const productAnalyticsContext = buildProductAnalyticsBpContext({
    product: 'distribution',
    feature: 'download',
    location: 'modal',
    modal_name: 'download_modal',
    flow: 'download_video_file',
    element: 'button',
    is_user_facing_data: false,
    entity_type: 'video',
    copy: null,
    device_type: getDeviceType(),
  });

  const thirdPartyIntegrationContext = buildThirdPartyIntegrationBpContext({
    is_integration: false,
    integration_id: null,
    integration_name: null,
    managed_user_id: null,
    app_id: null,
    integration_type: null,
    partner_bucket: null,
    is_partner: null,
  });

  const webBpContext = buildWebBpContext({
    page_name: pageName ?? 'viewer_home',
    referrer_page_name: location === 'svv' ? 'single_video_view' : null,
    target: null,
    target_path: null,
    path: path ?? null,
    copy: 'Download',
  });

  const teamRole = getTeamRole(teamUser);
  const teamContext = buildTeamBpContext({
    is_team_member: !!teamUser?.ownerId,
    team_owner_id: teamUser?.ownerId ?? null,
    team_id: teamUser?.teamId ?? null,
    team_subscription_type: teamUser?.accountType ?? null,
    team_size: teamUser?.currentTeamSize ?? null,
    team_role: teamRole,
  });

  const contexts = {
    ...actionContext,
    ...videoContext,
    ...productAnalyticsContext,
    ...teamContext,
    ...thirdPartyIntegrationContext,
    ...webBpContext,
  };
  const eventName = 'vimeo.distribute_content' as const;
  const version = 18;

  const fields: Fields<typeof eventName, typeof version> = {
    distribution_type: 'download',
    target_quality: quality,
    target_resolution: selectedResolution,
    target_file_details: null,
    number_of_items: 1,
    embed_config: null,
    embed_custom_dimensions: null,
    is_internal: false,
    target_transcript_language: null,
    audio_language_selected_for_download: audioTrackMeta.label,
    is_ai_translated: audioTrackMeta.isAIGenerated,
    is_original_language_downloaded: audioTrackMeta.isOriginalAudio,
    collection_type: null,
    distribution_tab: null,
    embed_layout: null,
    embed_playback_toggles_off: null,
    embed_playback_toggles_on: null,
    is_send_email_notification: null,
    lms_completion_threshold: null,
    lms_scoring_method: null,
    lms_technical_standard: null,
    sharee_email: null,
    sharee_entity_permission: null,
    sharee_id: null,
    sharee_team_permission: null,
  };

  sendBpEventWithContexts(eventName, contexts, version, fields);
};
